import React from 'react';
import { shape } from 'prop-types';
import { loadable, LazyHydrate as Hydrator } from 'nordic/lazy';
import View from '../view';

import Onboarding from '../../features/onboarding/onboarding.mobile';
import Highlights from '../../features/highlights/highlights';
import KYCNotification from '../../features/kyc-notification/kyc-notification.mobile';
import Gallery from '../../features/gallery/gallery.mobile';
import ActionLabel from '../../features/action-label/action-label.desktop';
import BillboardAdn from '../../features/billboard-adn/billboard-adn-bottom';
import RelatedSearches from '../../features/related-searches';
import FinanciableVehicles from '../../features/financiable-vehicles-label/financiable-vehicles-label.mobile';
import SmartTabs from '../../features/smart-tabs/smart-tabs';
import Grid from '../../../../../components/grid';
import ExtendedMenu from '../../features/extended-menu/extended-menu.mobile';
import DistanceWithBuyer from '../../features/distance-with-buyer';

// Head Section
const ItemStatusMessageHead = loadable(() => import('../../features/item-status/head'));
const Snackbar = loadable(() => import('../../features/snackbar'));

const RecommendationsCarouselFreeHorizontal = loadable(() =>
  import('../../features/recommendations/carousel-free-horizontal'),
);

const HeaderSellCta = loadable(() => import('../../features/header-sell-cta/header-sell-cta.mobile'));

// Hero
const Header = loadable(() => import('../../features/header/header.mobile'));
const SellerValidated = loadable(() => import('../../features/seller-validated/seller-validated'));

// Short Description
const VerifiedVehicles = loadable(() => import('../../features/verified-vehicles-tag/verified-vehicles-tag.mobile'));
const InspectionVehicles = loadable(() => import('../../features/inspection-vehicles-tag'));
const Price = loadable(() => import('../../features/price/price.mobile'));
const ItemStatusShortDescription = loadable(() => import('../../features/item-status/short-description'));
const GoodPriceTag = loadable(() => import('../../features/good-price-tag/good-price-tag'));
const PriceComparisonLink = loadable(() => import('../../features/price-comparison-link'));
const FinanceableTooltip = loadable(() => import('../../features/tooltips/financeable-tooltip'));
const GroupedMainActions = loadable(() => import('../../features/grouped-main-actions'));
const VehicleReservation = loadable(() => import('../../features/vehicle-reservation/return-policy/mobile'));
const VehicleReservationLegacy = loadable(() => import('../../features/vehicle-reservation-legacy/description/mobile'));
const GroupedShareBookmark = loadable(() => import('../../features/grouped-share-bookmark'));
const GiftRegistry = loadable(() => import('../../features/gift-registry'));
const BuyBenefits = loadable(() => import('../../features/buy-benefits/buy-benefits.mobile'));
const AvailableQuantity = loadable(() => import('../../features/available-quantity/available-quantity.mobile'));
const ReportProblem = loadable(() => import('../../features/report-problem/report-problem.mobile'));
const CreditOfferValue = loadable(() => import('../../features/credits-offer-value'));
const CreditsOfferBenefits = loadable(() => import('../../features/credits-offer-benefits'));
const CreditsOfferInfoLabel = loadable(() => import('../../features/credits-offer-info-label'));
const SalesTerms = loadable(() =>
  import('../../features/sales-terms-short-description/sales-terms-short-description.mobile'),
);

// Features, Description, PriceComparison, ExternalCredits
const VehiclePhysicalCondition = loadable(() => import('../../features/vehicle-physical-condition'));
const VehicleVerificationReport = loadable(() => import('../../features/vehicle-verification-report'));
const VehicleHistorySpecs = loadable(() => import('../../features/vehicle-history-specs'));
const VehicleInspectionSpecs = loadable(() => import('../../features/vehicle-inspection-specs'));
const VehicleInspectionFullSpecs = loadable(() => import('../../features/vehicle-inspection-full-specs'));
const HighlightedSaleSpecs = loadable(() => import('../../features/highlighted-sale-specs'));
const HighlightedSpecsAttrs = loadable(() => import('../../features/highlighted-specs/highlighted-specs-attrs.mobile'));
const AllFeatures = loadable(() => import('../../features/all-features/all-features.mobile'));
const Description = loadable(() => import('../../features/description/description.mobile'));
const PriceComparison = loadable(() => import('../../features/price-comparison'));
const ExternalCredits = loadable(() => import('../../features/external-credits'));
const PublishVehicleCta = loadable(() => import('../../features/publish-vehicle-cta'));
const BoxCreditProposal = loadable(() => import('../../features/box-credit-proposal'));
const VehicleCondition = loadable(() => import('../../features/vehicle-condition'));

// SellerInfo, ReservationInfo, Questions, SecurityTips
const SellerReputation = loadable(() => import('../../features/seller-reputation/seller-reputation'));
const SellerProfile = loadable(() => import('../../features/seller-profile/seller-profile.mobile'));
const SellerProfileHighlighted = loadable(() =>
  import('../../features/seller-profile-highlighted/seller-profile-highlighted.mobile'),
);
const SellerGoodAttentionHighlighted = loadable(() =>
  import('../../features/seller-good-attention/seller-good-attention-highlighted'),
);
const ReservationInfo = loadable(() => import('../../features/reservation-info'));
const Questions = loadable(() => import('../../features/questions-vis/questions-vis.mobile'));
const QuestionsUnregisteredMobile = loadable(() => import('../../features/questions/questions-unregistered-vip'));
const SecurityTips = loadable(() => import('../../features/security-tips/security-tips.mobile'));
const QuoterMotors = loadable(() => import('../../features/quoter-motors'));

// Content Bottom
const Denounce = loadable(() => import('../../features/denounce'));
const Breadcrumb = loadable(() => import('../../features/breadcrumb'));
const FullscreenAd = loadable(() => import('../../features/fullscreen-ad'));
const RelatedServices = loadable(() => import('../../features/related-services'));

// Recommendations
const RecommendationsCarouselFree = loadable(() => import('../../features/recommendations/carousel-free'));

const MotorsMobile = props => (
  <View {...props} vertical="motors">
    <Onboarding />
    <Grid.Container className="ui-pdp-container--top">
      {/* Head Section */}
      <Hydrator whenVisible>
        <ItemStatusMessageHead />
      </Hydrator>
      <Hydrator whenVisible>
        <HeaderSellCta />
      </Hydrator>
      <Hydrator whenIdle>
        <RecommendationsCarouselFree
          featureName="carousel_classifieds_motors_vip_paused"
          className="ui-pdp-carousel-not-active"
          lazy={false}
          preload
          fetchPriority="high"
          criticalImages={1}
        />
      </Hydrator>
      <Hydrator whenIdle>
        <RecommendationsCarouselFreeHorizontal
          featureName="vis_carousel_cheaper"
          className="ui-pdp-vis-carousel-cheaper pt-16 pb-20"
          lazy={false}
          preload
          fetchPriority="high"
          criticalImages={1}
        />
      </Hydrator>
      <Hydrator whenIdle>
        <RecommendationsCarouselFreeHorizontal
          featureName="carousel_classifieds_motors_vip_top"
          className="ui-pdp-vis-carousel-cheaper pt-16 pb-20"
          lazy={false}
          preload
          fetchPriority="high"
          criticalImages={1}
          arrowsSize="small"
        />
      </Hydrator>
      <Hydrator whenVisible>
        <KYCNotification />
      </Hydrator>
      {/* Hero */}
      <Hydrator whenIdle>
        <Header />
      </Hydrator>
      <Hydrator whenVisible>
        <CreditsOfferInfoLabel featureName="item_proximity" className="ui-pdp-proximity" />
      </Hydrator>
      <Hydrator whenVisible>
        <SellerValidated />
      </Hydrator>
      <Hydrator whenVisible>
        <VerifiedVehicles />
      </Hydrator>
      <Hydrator whenVisible>
        <InspectionVehicles />
      </Hydrator>
      <Gallery />
      {/* Short Description */}
      <Hydrator whenVisible>
        <Price />
      </Hydrator>
      <Hydrator whenVisible>
        <GoodPriceTag />
      </Hydrator>
      <Hydrator whenVisible>
        <FinanciableVehicles />
      </Hydrator>
      <Hydrator>
        <Highlights className="ui-pdp-highlights" />
      </Hydrator>
      <Hydrator whenVisible>
        <CreditOfferValue />
      </Hydrator>
      <Hydrator whenVisible>
        <CreditsOfferBenefits />
      </Hydrator>
      <Hydrator whenVisible>
        <PriceComparisonLink />
      </Hydrator>
      <Hydrator whenVisible>
        <SalesTerms />
      </Hydrator>
      <Hydrator whenVisible>
        <ItemStatusShortDescription />
      </Hydrator>
      <Hydrator whenVisible>
        <BoxCreditProposal />
      </Hydrator>
      <Hydrator whenVisible>
        <ActionLabel />
      </Hydrator>
      <Hydrator whenVisible>
        <FinanceableTooltip />
      </Hydrator>
      <Hydrator whenVisible>
        <VehicleReservation />
      </Hydrator>
      <Hydrator whenVisible>
        <VehicleReservationLegacy />
      </Hydrator>
      <Hydrator whenVisible>
        <DistanceWithBuyer />
      </Hydrator>
      <Hydrator whenVisible>
        <GroupedMainActions />
      </Hydrator>
      <Hydrator whenVisible>
        <SellerGoodAttentionHighlighted />
      </Hydrator>
      <Hydrator whenVisible>
        <CreditsOfferInfoLabel featureName="credits_offer_landing" />
      </Hydrator>
      <Hydrator whenVisible>
        <CreditsOfferInfoLabel featureName="vis_credits_proximity" className="ui-pdp-proximity" />
      </Hydrator>
      <Hydrator whenVisible>
        <ReportProblem />
      </Hydrator>
      <Hydrator whenVisible>
        <GiftRegistry className="mt-24" />
        <GroupedShareBookmark />
      </Hydrator>
      <Hydrator whenIdle>
        <RecommendationsCarouselFree
          featureName="vis_similar_financial_offer_carousel"
          className="ui-pdp-vis-carousel-b2c"
          lazy={false}
          preload
          fetchPriority="high"
          criticalImages={1}
          paddingHorizontal={20}
        />
      </Hydrator>
      <Hydrator whenIdle>
        <RecommendationsCarouselFree
          featureName="vis_similar_financial_motors_carousel"
          className="ui-pdp-vis-carousel-b2c"
          lazy={false}
          preload
          fetchPriority="high"
          criticalImages={1}
          paddingHorizontal={20}
        />
      </Hydrator>
      <Hydrator whenIdle>
        <RecommendationsCarouselFree
          featureName="carousel_classifieds_motors_vip_bau"
          className="ui-pdp-vis-carousel-b2c"
          lazy={false}
          preload
          fetchPriority="high"
          criticalImages={1}
          paddingHorizontal={20}
        />
      </Hydrator>
      <Hydrator whenIdle>
        <RecommendationsCarouselFree
          featureName="carousel_classifieds_motors_vip_fsbo"
          className="ui-pdp-vis-carousel-b2c"
          lazy={false}
          preload
          fetchPriority="high"
          criticalImages={1}
          paddingHorizontal={20}
        />
      </Hydrator>
      <Hydrator whenVisible>
        <BuyBenefits />
      </Hydrator>
      <Hydrator whenVisible>
        <AvailableQuantity />
      </Hydrator>
      {/* Features, Description, PriceComparison, ExternalCredits */}
      <Hydrator whenVisible>
        <VehiclePhysicalCondition />
      </Hydrator>
      <Hydrator whenVisible>
        <VehicleCondition />
      </Hydrator>
      <Hydrator whenVisible>
        <VehicleVerificationReport />
      </Hydrator>
      <Hydrator whenVisible>
        <VehicleHistorySpecs />
      </Hydrator>
      <Hydrator whenVisible>
        <VehicleInspectionSpecs />
      </Hydrator>
      <Hydrator whenVisible>
        <VehicleInspectionFullSpecs />
      </Hydrator>
      <Hydrator whenVisible>
        <RecommendationsCarouselFree
          featureName="vis_vpp_carousel"
          className="ui-pdp-vis-carousel-b2c vis-vpp-carousel"
          preload
          fetchPriority="high"
          criticalImages={1}
          paddingHorizontal={20}
        />
      </Hydrator>
      <Hydrator whenVisible>
        <HighlightedSaleSpecs />
      </Hydrator>
      <Hydrator whenVisible>
        <HighlightedSpecsAttrs />
      </Hydrator>
      <Hydrator whenVisible>
        <AllFeatures />
      </Hydrator>
      <Hydrator whenVisible>
        <Description className="pl-16 pr-16" />
      </Hydrator>
      <Hydrator whenIdle>
        <RecommendationsCarouselFree
          featureName="carousel_classifieds_motors_vip_historical_securedloan"
          className="ui-pdp-vis-carousel-b2c motors-historical-securedloan"
          lazy={false}
          preload
          fetchPriority="high"
          criticalImages={1}
          paddingHorizontal={20}
        />
      </Hydrator>
      <Hydrator whenIdle>
        <PriceComparison />
      </Hydrator>
      <Hydrator whenVisible>
        <ExternalCredits />
      </Hydrator>
      {/* SellerInfo, ReservationInfo, Questions, SecurityTips */}
      <Hydrator whenIdle>
        <SellerProfileHighlighted />
      </Hydrator>
      <Hydrator whenIdle>
        <SellerProfile />
      </Hydrator>
      <Hydrator whenVisible>
        <SellerReputation />
      </Hydrator>
      <Hydrator whenVisible>
        <ReservationInfo />
      </Hydrator>
      <Hydrator whenIdle>
        <Questions />
      </Hydrator>
      <Hydrator whenVisible>
        <QuestionsUnregisteredMobile />
      </Hydrator>
      <Hydrator whenVisible>
        <QuoterMotors />
      </Hydrator>
      <Hydrator whenVisible>
        <PublishVehicleCta />
      </Hydrator>
      <Hydrator whenVisible>
        <SecurityTips />
      </Hydrator>
      <Hydrator whenIdle>
        <RelatedServices />
      </Hydrator>
      {/* Content Bottom */}
      <div className="ui-pdp-container--bottom">
        <Hydrator whenVisible>
          <Denounce />
        </Hydrator>
        <Hydrator whenVisible>
          <RecommendationsCarouselFree featureName="carousel_classifieds_motors_vip" className="mb-24 mt-16" />
        </Hydrator>
        <Hydrator whenVisible>
          <RecommendationsCarouselFree
            featureName="carousel_classifieds_motors_vip_seller_items"
            className="mb-24 mt-16"
          />
        </Hydrator>
        <Hydrator whenVisible>
          <RelatedSearches />
        </Hydrator>
        <Hydrator whenVisible>
          <Breadcrumb />
        </Hydrator>
        <Hydrator whenIdle>
          <FullscreenAd />
        </Hydrator>
        <Hydrator ssrOnly>
          <BillboardAdn />
        </Hydrator>
        <Hydrator whenVisible>
          <SmartTabs />
        </Hydrator>
        <Hydrator whenVisible>
          <ExtendedMenu />
        </Hydrator>
      </div>
      <Snackbar />
    </Grid.Container>
  </View>
);

MotorsMobile.propTypes = {
  initialState: shape().isRequired,
};

export default MotorsMobile;
